import { useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { AppContext } from './context'
import { supabase } from '../lib/supabase'
import type {
  AppState,
  UserProfile,
  Property,
  TenantLink,
  Payment,
  MaintenanceTicket,
} from './types'
import { generateId } from '../utils/formatters'
import { getEffectivePlan } from '../lib/plans'

export function AppProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AppState>({
    properties: [],
    tenantLinks: [],
    payments: [],
    tickets: [],
  })

  const [user, setUser] = useState<UserProfile>({
    id: '',
    name: '',
    email: '',
    plan: 'Free',
    role: 'landlord',
    isAuthenticated: false,
    isOnboarded: true,
  })

  const [authLoading, setAuthLoading] = useState(true)

  const loadProfile = async (sessionUser: { id: string; email?: string | null; user_metadata?: Record<string, any> }) => {
    console.log('[AUTH] loading profile for', sessionUser.id)

    // user_metadata.role is written at sign-up time and is the ground truth.
    // The DB profile may lag (trigger hasn't fired, or was created with wrong role).
    const metaRole = sessionUser.user_metadata?.role as 'landlord' | 'tenant' | undefined

    const { data: profile, error } = await supabase
      .from('profiles')
      .select('email, role, plan, stripe_customer_id, subscription_status')
      .eq('id', sessionUser.id)
      .maybeSingle()

    console.log('[AUTH] profile result', { profile, error, metaRole })

    if (error) {
      console.error('[AUTH] error loading profile', error)
    }

    // ── No profile row yet ────────────────────────────────────────────────
    if (!profile) {
      const roleFromMeta = metaRole || 'landlord'
      console.log('[AUTH] creating profile with role:', roleFromMeta)
      await supabase.from('profiles').upsert({
        id: sessionUser.id,
        email: sessionUser.email || '',
        role: roleFromMeta,
      })

      setUser({
        id: sessionUser.id,
        name: sessionUser.user_metadata?.name || sessionUser.email?.split('@')[0] || 'User',
        email: sessionUser.email || '',
        plan: 'Free',
        role: roleFromMeta,
        isAuthenticated: true,
        isOnboarded: true,
      })
      return
    }

    // ── Profile exists — determine effective role ──────────────────────────
    // If metadata says 'tenant' but DB says 'landlord', the profile was
    // created incorrectly (trigger race / wrong default). Heal it now.
    let effectiveRole: 'landlord' | 'tenant' = profile.role || 'landlord'

    if (metaRole === 'tenant' && effectiveRole !== 'tenant') {
      console.log('[AUTH] healing profile role: landlord → tenant')
      await supabase
        .from('profiles')
        .update({ role: 'tenant' })
        .eq('id', sessionUser.id)
      effectiveRole = 'tenant'
    }

    // Effective plan: drops to Free if sub status is canceled/past_due/unpaid/expired.
    // This is a client-side safety net — the Stripe webhook is the primary enforcement.
    const effectivePlan = getEffectivePlan(profile.plan, profile.subscription_status)

    setUser({
      id: sessionUser.id,
      name: sessionUser.user_metadata?.name || sessionUser.email?.split('@')[0] || 'User',
      email: sessionUser.email || '',
      plan: effectivePlan,
      role: effectiveRole,
      isAuthenticated: true,
      isOnboarded: true,
      stripe_customer_id: profile.stripe_customer_id ?? null,
      subscription_status: profile.subscription_status ?? null,
    })
  }

  useEffect(() => {
    let mounted = true

    const initAuth = async () => {
      try {
        console.log('[AUTH] initializing...')
        setAuthLoading(true)

        const {
          data: { session },
          error,
        } = await supabase.auth.getSession()

        console.log('[AUTH] getSession result', { session, error })

        if (error) throw error

        if (!session?.user) {
          if (mounted) {
            setUser({
              id: '',
              name: '',
              email: '',
              plan: 'Free',
              role: 'landlord',
              isAuthenticated: false,
              isOnboarded: false,
            })
          }
          return
        }

        if (mounted) {
          await loadProfile(session.user)
        }
      } catch (err) {
        console.error('[AUTH] initAuth error', err)
        if (mounted) {
          setUser({
            id: '',
            name: '',
            email: '',
            plan: 'Free',
            role: 'landlord',
            isAuthenticated: false,
            isOnboarded: false,
          })
        }
      } finally {
        if (mounted) {
          setAuthLoading(false)
        }
      }
    }

    initAuth()

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('[AUTH] onAuthStateChange event:', event)

      setTimeout(async () => {
        if (!mounted) return

        try {
          if (!session?.user) {
            setUser({
              id: '',
              name: '',
              email: '',
              plan: 'Free',
              role: 'landlord',
              isAuthenticated: false,
              isOnboarded: false,
            })
            setAuthLoading(false)
            return
          }

          setAuthLoading(true)
          await loadProfile(session.user)
        } catch (err) {
          console.error('[AUTH] onAuthStateChange error', err)
          if (session?.user) {
            // Use metadata role so tenants are never mis-routed on profile load error
            const fallbackRole =
              (session.user.user_metadata?.role as 'landlord' | 'tenant') || 'landlord'
            setUser({
              id: session.user.id,
              name: session.user.user_metadata?.name || session.user.email?.split('@')[0] || 'User',
              email: session.user.email || '',
              plan: 'Free',
              role: fallbackRole,
              isAuthenticated: true,
              isOnboarded: true,
            })
          } else {
            setUser({
              id: '',
              name: '',
              email: '',
              plan: 'Free',
              role: 'landlord',
              isAuthenticated: false,
              isOnboarded: false,
            })
          }
        } finally {
          setAuthLoading(false)
        }
      }, 0)
    })

    return () => {
      mounted = false
      subscription.unsubscribe()
    }
  }, [])

  const login = () => {}

  const logout = async () => {
    await supabase.auth.signOut()
    setUser({
      id: '',
      name: '',
      email: '',
      plan: 'Free',
      role: 'landlord',
      isAuthenticated: false,
      isOnboarded: false,
    })
  }

  const completeOnboarding = () =>
    setUser((prev) => ({ ...prev, isOnboarded: true }))

  const updateProfile = async (updates: Partial<Pick<import('./types').UserProfile, 'name'>>) => {
    if (!user.id) return
    if (updates.name !== undefined) {
      await supabase.auth.updateUser({ data: { name: updates.name } })
      await supabase.from('profiles').update({ full_name: updates.name }).eq('id', user.id)
    }
    setUser((prev) => ({ ...prev, ...updates }))
  }

  const addProperty = (propertyData: Omit<Property, 'id'>) => {
    const newProperty = { ...propertyData, id: generateId('prop') }
    setState((prev) => ({ ...prev, properties: [newProperty, ...prev.properties] }))
    return newProperty.id
  }

  const updateProperty = (id: string, updates: Partial<Property>) => {
    setState((prev) => ({
      ...prev,
      properties: prev.properties.map((p) => (p.id === id ? { ...p, ...updates } : p)),
    }))
  }

  const addTenant = (tenantData: Omit<TenantLink, 'id'>) => {
    const newTenant = { ...tenantData, id: generateId('tenant') }
    setState((prev) => ({ ...prev, tenantLinks: [newTenant as TenantLink, ...prev.tenantLinks] }))
    return newTenant.id
  }

  const updateTenant = (id: string, updates: Partial<TenantLink>) => {
    setState((prev) => ({
      ...prev,
      tenantLinks: prev.tenantLinks.map((t) => (t.id === id ? { ...t, ...updates } : t)),
    }))
  }

  const addPayment = (paymentData: Omit<Payment, 'id'>) => {
    const newPayment = { ...paymentData, id: generateId('pay') }
    setState((prev) => ({ ...prev, payments: [newPayment, ...prev.payments] }))
    return newPayment.id
  }

  const updatePaymentStatus = (id: string, status: Payment['status']) => {
    setState((prev) => ({
      ...prev,
      payments: prev.payments.map((p) => {
        if (p.id !== id) return p
        const paidDate =
          status === 'paid' && p.status !== 'paid'
            ? new Date().toISOString().split('T')[0]
            : p.paidDate
        return { ...p, status, paidDate }
      }),
    }))
  }

  const addTicket = (ticketData: Omit<MaintenanceTicket, 'id'>) => {
    const newTicket = { ...ticketData, id: generateId('ticket') }
    setState((prev) => ({ ...prev, tickets: [newTicket as MaintenanceTicket, ...prev.tickets] }))
    return newTicket.id
  }

  const updateTicketStatus = (id: string, status: MaintenanceTicket['status']) => {
    setState((prev) => ({
      ...prev,
      tickets: prev.tickets.map((t) => {
        if (t.id !== id) return t
        return { ...t, status }
      }),
    }))
  }

  const inviteTenant = (_propertyId: string, _email: string) => 'INVITE-CODE'
  const acceptInvite = (_inviteCode: string, _userDetails: { name: string; email: string }) => true

  const value = useMemo(
    () => ({
      state,
      user,
      authLoading,
      login,
      logout,
      completeOnboarding,
      updateProfile,
      addProperty,
      updateProperty,
      addTenant,
      updateTenant,
      addPayment,
      updatePaymentStatus,
      addTicket,
      updateTicketStatus,
      inviteTenant,
      acceptInvite,
    }),
    [state, user, authLoading]
  )

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>
}
